
import { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from '../../../i18n/translations';

type TabKey = 'ngfw' | 'vm' | 'ippbx';

function NgfwTab() {
  const { language } = useLanguage();
  const t = translations[language].techAnalysis.ngfw;

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.title}</h3>
        <p className="text-gray-500 text-sm">{t.subtitle}</p>
      </div>
      <div className="overflow-x-auto rounded-xl shadow-md">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="bg-gray-800 text-white px-6 py-4 text-left font-semibold w-36">{t.col1}</th>
              <th className="bg-gray-700 text-white px-6 py-4 text-left font-semibold">
                <div className="flex items-center space-x-2">
                  <i className="ri-close-circle-fill text-red-400 text-base"></i>
                  <span>{t.col2}</span>
                </div>
              </th>
              <th className="bg-gray-700 text-white px-6 py-4 text-left font-semibold">
                <div className="flex items-center space-x-2">
                  <i className="ri-error-warning-fill text-yellow-400 text-base"></i>
                  <span>{t.col3}</span>
                </div>
              </th>
              <th className="bg-[#00A3FF] text-white px-6 py-4 text-left font-semibold">
                <div className="flex items-center space-x-2">
                  <i className="ri-checkbox-circle-fill text-white text-base"></i>
                  <span>{t.col4}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 font-semibold text-gray-800 border-b border-gray-100">{row.dimension}</td>
                <td className="px-6 py-4 text-gray-500 border-b border-gray-100">
                  <div className="flex items-start space-x-2">
                    <i className="ri-close-circle-fill text-red-400 mt-0.5 flex-shrink-0"></i>
                    <span>{row.traditional}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500 border-b border-gray-100">
                  <div className="flex items-start space-x-2">
                    <i className="ri-error-warning-fill text-yellow-400 mt-0.5 flex-shrink-0"></i>
                    <span>{row.utm}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-800 font-medium border-b border-gray-100 bg-blue-50">
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-[#00A3FF] mt-0.5 flex-shrink-0"></i>
                    <span>{row.ngfw}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start space-x-3">
        <i className="ri-information-line text-[#00A3FF] text-xl flex-shrink-0 mt-0.5"></i>
        <p className="text-sm text-gray-600">{t.tip}</p>
      </div>
    </div>
  );
}

function VmTab() {
  const { language } = useLanguage();
  const t = translations[language].techAnalysis.vm;

  const colors = ['#00A3FF', '#10B981', '#F59E0B', '#EF4444'];
  const icons = ['ri-stack-line', 'ri-refresh-line', 'ri-shield-check-line', 'ri-lock-line'];
  const statIcons = ['ri-money-dollar-circle-line', 'ri-time-line', 'ri-stack-line'];
  const statColors = ['#10B981', '#00A3FF', '#F59E0B'];

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.title}</h3>
        <p className="text-gray-500 text-sm">{t.subtitle}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {t.items.map((adv, i) => (
          <div key={i} className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: colors[i] + '18' }}>
                <i className={`${icons[i]} text-xl`} style={{ color: colors[i] }}></i>
              </div>
              <h4 className="text-base font-bold text-gray-900">{adv.title}</h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{adv.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {t.statLabels.map((label, i) => (
          <div key={i} className="bg-white rounded-xl p-5 shadow-md border border-gray-100 text-center">
            <div className="w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-3" style={{ backgroundColor: statColors[i] + '18' }}>
              <i className={`${statIcons[i]} text-xl`} style={{ color: statColors[i] }}></i>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{t.statValues[i]}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IpPbxTab() {
  const { language } = useLanguage();
  const t = translations[language].techAnalysis.ippbx;

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.title}</h3>
        <p className="text-gray-500 text-sm">{t.subtitle}</p>
      </div>
      <div className="overflow-x-auto rounded-xl shadow-md">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="bg-gray-800 text-white px-6 py-4 text-left font-semibold w-40">{t.col1}</th>
              <th className="bg-gray-700 text-white px-6 py-4 text-left font-semibold">
                <div className="flex items-center space-x-2">
                  <i className="ri-close-circle-fill text-red-400 text-base"></i>
                  <span>{t.col2}</span>
                </div>
              </th>
              <th className="bg-[#00A3FF] text-white px-6 py-4 text-left font-semibold">
                <div className="flex items-center space-x-2">
                  <i className="ri-checkbox-circle-fill text-white text-base"></i>
                  <span>{t.col3}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 font-semibold text-gray-800 border-b border-gray-100">{row.dimension}</td>
                <td className="px-6 py-4 text-gray-500 border-b border-gray-100">
                  <div className="flex items-start space-x-2">
                    <i className="ri-close-circle-fill text-red-400 mt-0.5 flex-shrink-0"></i>
                    <span>{row.keyline}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-800 font-medium border-b border-gray-100 bg-blue-50">
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-[#00A3FF] mt-0.5 flex-shrink-0"></i>
                    <span>{row.ippbx}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start space-x-3">
        <i className="ri-information-line text-[#00A3FF] text-xl flex-shrink-0 mt-0.5"></i>
        <p className="text-sm text-gray-600">{t.tip}</p>
      </div>
    </div>
  );
}

export default function TechAnalysis() {
  const [activeTab, setActiveTab] = useState<TabKey>('vm');
  const { language } = useLanguage();
  const t = translations[language].techAnalysis;

  const tabs: { key: TabKey; icon: string }[] = [
    { key: 'ngfw', icon: 'ri-shield-flash-line' },
    { key: 'vm', icon: 'ri-server-2-line' },
    { key: 'ippbx', icon: 'ri-phone-line' },
  ];

  const tabLabels: Record<TabKey, string> = {
    ngfw: t.tabs.ngfw,
    vm: t.tabs.vm,
    ippbx: t.tabs.ippbx,
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'ngfw': return <NgfwTab />;
      case 'vm': return <VmTab />;
      case 'ippbx': return <IpPbxTab />;
      default: return null;
    }
  };

  return (
    <section id="tech-analysis" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-block px-4 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold tracking-wider rounded-full mb-4">
            {t.badge}
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-10 justify-center">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-[#00A3FF] text-white shadow-md shadow-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              <span>{tabLabels[tab.key]}</span>
            </button>
          ))}
        </div>

        {/* Content Panel */}
        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 shadow-sm">
          {renderContent()}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500 mb-4">{t.ctaText}</p>
          <a
            href="#contact"
            className="inline-flex items-center space-x-2 px-8 py-3.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>{t.ctaBtn}</span>
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
